// Central content + data for the Upword Media marketing site.

// Client marquee: prefer real logo files under /logos/. `file` = null falls back
// to colored wordmark. `bg` optional flag hints at very small favicon sources
// that should render inside a subtle frame.
export const CLIENTS = [
  { name: "SaaS Labs", file: "/logos/saaslabs.png" },
  { name: "Airmeet", file: "/logos/airmeet.png" },
  { name: "Shift AltCap", file: "/logos/shiftaltcap.webp" },
  { name: "Capillary Technologies", file: "/logos/capillary.png" },
  { name: "Listnr AI", file: "/logos/listnr.png" },
  { name: "BambooBox", file: "/logos/bamboobox.png" },
  { name: "inFeedo", file: "/logos/infeedo.png" },
  { name: "Recur Club", file: "/logos/recurclub.png" },
];

export const PAIN_VS_FIX = [
  {
    point: "Generating Traffic",
    challenge: "Content sits on page 5 of Google and never gets found.",
    fix: "SEO-first content engineered to actually rank.",
  },
  {
    point: "Getting Cited by AI Engines",
    challenge: "Invisible to ChatGPT, Claude, Perplexity and other AI Engines when buyers ask for recommendations.",
    fix: "AEO/GEO optimization built into every piece we write.",
  },
  {
    point: "Attracting Quality Leads",
    challenge: "Traffic comes in, but leads don't convert.",
    fix: "Content written for buyer intent, not vanity clicks.",
  },
  {
    point: "Hiring Writing Talent",
    challenge: "Months lost hiring/ vetting writers who understand Tech, your industry and your buyers.",
    fix: "A vetted team of senior SaaS writers, ready from day one.",
  },
  {
    point: "Measuring KPIs & ROI",
    challenge: "Reports full of pageviews, no line to revenue.",
    fix: "Reporting tied to pipeline and revenue impact.",
  },
  {
    point: "Having a Real Strategy",
    challenge: "Content gets published without a goal and strategy.",
    fix: "Research-backed roadmap tied to buyer stages and business goal.",
  },
];

export const SERVICES_TEASER = [
  { n: "01", title: "Content Strategy", line: "Auditing, messaging & positioning, buyer-journey mapping, editorial strategy, and a 90-day roadmap built on real research.", anchor: "content-strategy" },
  { n: "02", title: "SEO/AEO/GEO-Optimized Content", line: "Content engineered to rank in Google and get cited by AI search.", anchor: "blog-content" },
  { n: "03", title: "Conversion-Focused Assets", line: "Landing pages, case studies, whitepapers, and guides built to move buyers to a decision.", anchor: "conversion-assets" },
  { n: "04", title: "Content Operations & Scaling", line: "Playbooks and workflows that make content repeatable at scale.", anchor: "content-ops" },
  { n: "05", title: "Social Media & Community", line: "Organic social that builds category presence and feeds the funnel.", anchor: "social" },
  { n: "06", title: "Email & Lifecycle Marketing", line: "Newsletters and nurture sequences that keep leads warm.", anchor: "email" },
];

export const DANDELION = [
  { n: "01", title: "Strategic Foundation", desc: "We start with your buyers, your category, and your pipeline math — not a keyword dump. Positioning, ICP, and the questions your market is actually asking." },
  { n: "02", title: "Content Architecture", desc: "Pillar-and-cluster structure mapped to the buyer journey, so every piece has a job and a place in the system." },
  { n: "03", title: "Multi-Channel Roadmap", desc: "One roadmap engineered for SEO, AEO, and GEO — content built to rank in Google and get cited by ChatGPT, Perplexity, and Claude." },
  { n: "04", title: "Plant the Seeds", desc: "High-velocity production kicks in — research-backed articles, assets, social, and email shipping on a consistent cadence." },
  { n: "05", title: "Roots & Growth", desc: "Content starts interlinking and ranking. We tune, refresh, and double down on what's earning attention and pipeline." },
  { n: "06", title: "Compounding Effect", desc: "The system runs itself. Traffic, citations, and qualified demand compound month over month instead of resetting." },
];

export const STAGES = [
  {
    stage: "Early-Stage / Post-PMF",
    challenge: "You've found product-market fit but your content doesn't reflect it yet.",
    delivers: "We build the foundational content engine: strategy, core pillar pages, and the first 90 days of consistent publishing.",
  },
  {
    stage: "Growth-Stage",
    challenge: "You're scaling fast and content can't keep up.",
    delivers: "We scale production without sacrificing quality — multi-channel output (blog, social, email) running as a system, not one-off asks.",
  },
  {
    stage: "Enterprise / Category Leader",
    challenge: "You need content that matches your market position.",
    delivers: "We run full-scale, multi-channel content operations across social, email, SMS, and blog — the kind of partnership we've run with Capillary Technologies for 2+ years.",
  },
];

export const TESTIMONIALS = [
  {
    quote: "Upword Media exceeded my expectations in every area. They consistently deliver high-quality work across a diverse range of content formats, including emails, newsletters, decks, blogs, and ebooks. Their reliability and expertise make them an invaluable partner. Highly recommended!",
    name: "Tahseen Kazi",
    role: "Director, Content Strategy, inFeedo",
  },
  {
    quote: "Outstanding content service by Upword Media. The quality and effectiveness of the work have truly exceeded our expectations.",
    name: "Adarsh Rana",
    role: "Marketing Manager, Capillary Technologies",
  },
];

export const RESULTS = [
  { stat: "9+ yrs", label: "Experience in B2B SaaS and tech growth marketing." },
  { stat: "100+", label: "Brands partnered with — including Capillary Technologies, BambooBox, inFeedo, Listnr AI, Recur Club, and Airmeet." },
  { stat: "2.5+ yrs", label: "Average length of our client partnerships." },
  { stat: "Page 1", label: "SERP rankings achieved for long-term clients on their most relevant keywords." },
];

export const FAQS = [
  {
    q: "What does it cost to work with Upword Media?",
    a: "Straight answer, no \"contact us for pricing\" runaround. One-time content pieces run from $200–$500 depending on format and depth. Monthly retainers run from $2,500 to $5,000+ depending on scope and volume. Your exact number is set after a short scoping call — no surprises later.",
  },
  {
    q: "Do I need to sign a long-term contract?",
    a: "No lock-in. We work on a rolling monthly basis after an initial 3-month minimum term — long enough for compounding content to actually show results, short enough that we have to keep earning your business every month.",
  },
  {
    q: "Will this replace my in-house marketing team?",
    a: "No — Upword extends what an in-house team can do. We handle high-velocity content production and strategy so your internal marketers stay focused on brand, product, and stakeholder work. Think added horsepower, not a replacement.",
  },
  {
    q: "How do you make sure content doesn't read like generic AI output?",
    a: "Every piece starts with real research — subject-matter interviews, original data, and product depth — then runs through a human editorial layer for voice, accuracy, and point of view. AI accelerates drafting and structure; humans own the thinking, the sources, and the final word. That's why our clients recognize us for research-driven, first-draft quality.",
  },
  {
    q: "How soon will we see results?",
    a: "Honest, by channel: paid and social can show early engagement within weeks. SEO and AEO content typically take 3–6 months to build compounding momentum — consistent with what we've seen with clients like Airmeet and Capillary. We plant seeds that keep paying off long after they're published.",
  },
];

export const SERVICES = [
  {
    id: "content-strategy",
    n: "01",
    title: "Strategic Content Planning",
    desc: "The blueprint before the build. We map your buyers, your category, and the exact questions driving demand — then turn it into a roadmap your team can run.",
    deliverables: ["Buyer journey mapping", "Keyword & topic clustering", "Pillar-cluster architecture", "90-day content roadmap", "Competitive analysis", "AI search (AEO/GEO) optimization strategy"],
    investment: "$4,500–$6,500",
    timeline: "2–3 weeks",
  },
  {
    id: "blog-content",
    n: "02",
    title: "SEO-Optimized Blog Content",
    desc: "Long-form articles engineered to rank in Google and get cited by AI engines — research-backed, on-brand, and built to convert, not just to fill a calendar.",
    deliverables: ["2,000–3,500 word articles", "On-page SEO", "AEO optimization", "Conversion-focused CTAs", "Internal linking strategy"],
    investment: "$800–$1,200 / article  ·  $4,500–$15,000+ / mo retainer",
    timeline: "7–10 days per article",
  },
  {
    id: "conversion-assets",
    n: "03",
    title: "Conversion-Focused Assets",
    desc: "The bottom-of-funnel firepower. Assets built to move buyers to a decision — proof, positioning, and comparison in one place.",
    deliverables: ["Case studies", "Whitepapers & eBooks", "Comparison guides", "Landing pages", "Email sequences"],
  },
  {
    id: "content-ops",
    n: "04",
    title: "Content Operations & Scaling",
    desc: "The systems that make content repeatable long after we're gone — so your team ships fast without reinventing the process each time.",
    deliverables: ["Brand playbook", "Templates library", "Workflow automation", "QA frameworks", "Team training", "Tracking dashboards"],
    investment: "$6,500–$9,500",
    timeline: "4–6 weeks",
  },
  {
    id: "social",
    n: "05",
    title: "Social Media & Community",
    desc: "Organic social strategy and content that builds category presence and feeds the top of the funnel — where your buyers already spend their attention.",
    deliverables: ["Organic social strategy", "Founder & brand content", "Editorial calendar", "Community engagement", "Repurposing engine"],
    investment: "Custom to scope",
    timeline: "Ongoing",
  },
  {
    id: "email",
    n: "06",
    title: "Email & Lifecycle Marketing",
    desc: "Newsletters and nurture sequences that keep leads warm between content touchpoints and sales conversations — so demand doesn't go cold.",
    deliverables: ["Newsletter program", "Nurture sequences", "Lifecycle flows", "Segmentation strategy", "Copy & design"],
    investment: "Custom to scope",
    timeline: "Ongoing",
  },
];

export const CASE_STUDIES = [
  {
    id: "ai-voice-platform",
    client: "An AI-powered communication platform",
    tag: "AI Voice · Anonymized",
    context: "AI voice technology company scaling across dozens of distinct use cases.",
    challenge: "Needed to establish visibility and conversions across a large number of distinct AI voice use cases, refresh dated website content, and back thought leadership with original research.",
    solution: "Built and shipped 120+ dedicated landing pages — one per AI voice use case — alongside ongoing blog articles, a full website content refresh, and an original research paper to support engagement.",
    result: "120+ use-case landing pages live within 3 months, improved conversions across the site, refreshed core website copy, and a published research paper driving engagement.",
    metrics: [{ v: "120+", l: "Landing pages in 3 months" }, { v: "1", l: "Original research paper" }],
    quote: null,
  },
  {
    id: "capillary",
    client: "Capillary Technologies",
    tag: "Enterprise · 2+ Year Partnership",
    context: "Industry-leading loyalty management platform — India's largest B2B SaaS company by category, recently IPO'd.",
    challenge: "Needed a single content partner who could run strategy and execution across every channel — not just blog content — to grow traffic and engagement at enterprise scale.",
    solution: "Ran end-to-end content strategy across social, email, SMS, and blog articles as one coordinated system.",
    result: "Improved traffic across channels and sustained as a trusted partner for 2+ years.",
    metrics: [{ v: "2+ yrs", l: "Retained partnership" }, { v: "4", l: "Channels run end-to-end" }],
    quote: {
      text: "I would recommend anyone and everyone looking out for Content related solutions. There are very few in the market who provide 'research-driven' and organic content and Upword Media is definitely one amongst them!",
      name: "Adarsh Rana",
      role: "Marketing Manager, Capillary Technologies",
    },
  },
  {
    id: "infeedo",
    client: "inFeedo",
    tag: "People Experience · Multi-format",
    context: "Conversational people-experience platform.",
    challenge: "Needed to scale research-driven content to increase website engagement, across a range of formats.",
    solution: "Produced research-driven content across emails, newsletters, decks, blogs, and ebooks.",
    result: "Increased website engagement; recognized for reliability, first-draft quality, and process rigor across a diverse content mix.",
    metrics: [{ v: "5+", l: "Content formats delivered" }, { v: "↑", l: "Website engagement" }],
    quote: {
      text: "When hiring a content agency, I always start with the basics—portfolio, samples, and metrics. However, what truly sets an agency apart for me are key factors such as timely delivery of projects, attention to detail, quality of the first draft, a well-structured brief template, and their level of involvement in the process. Upword Media exceeded my expectations in every area. They consistently deliver high-quality work across a diverse range of content formats, including emails, newsletters, decks, blogs, and ebooks. Their reliability and expertise make them an invaluable partner. Highly recommended!",
      name: "Tahseen Kazi",
      role: "Director, Content Strategy, inFeedo",
    },
  },
  {
    id: "airmeet",
    client: "Airmeet",
    tag: "Virtual Events · 250+ Pieces",
    context: "Fast-growing virtual events platform (launched late 2019), trusted by 6,550+ organizations including Philips, Walmart, Volvo, and Forbes. 4.7/5 on G2, 11,000+ events hosted.",
    challenge: "Needed content for every stage of the marketing funnel at scale while accelerating growth and preparing to raise funding.",
    solution: "Delivered 250+ pieces of content over 2 years — 150+ blog articles across 10 content pillars (knowledge-hub, use cases, product guides, comparisons), 75+ case studies, 11 ebooks, Quora responses, affiliate blogs, SEO articles, and video scripts for use-case marketing videos.",
    result: "Content helped accelerate growth and traffic; the platform now ranks on page 1 of SERPs for several of its most relevant keywords.",
    metrics: [{ v: "250+", l: "Content pieces in 2 years" }, { v: "Page 1", l: "SERP rankings" }],
    quote: {
      text: "It's been a wonderful experience working with Upword for all our content requirements. The team is professional, creative and have a quick turnaround time. The right blend of characteristics one would need from a content partner.",
      name: "Senior Marketing Executive",
      role: "Airmeet",
    },
  },
];

export const CHALLENGE_OPTIONS = [
  "We publish, but nothing ranks",
  "Content can't keep up with growth",
  "No content strategy at all",
  "Quality drops when we scale",
  "Not getting cited by AI search",
  "Content doesn't drive pipeline",
  "Something else",
];

// Booking slots (IST business hours, Mon–Fri only).
export const BOOKING_TIMES = ["10:00 AM", "11:00 AM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM"];
export const BOOKING_TZ = "Asia/Kolkata (IST)";
